/**
 * Formats a WhatsApp phone number to be user-friendly
 * @param phoneNumber - Raw phone number like "919988306538@c.us"
 * @returns Formatted phone number like "+91 9988 306 538"
 */
export const formatPhoneNumber = (phoneNumber: string): string => {
  if (!phoneNumber) return 'Unknown';
  
  // Remove @c.us or @g.us suffix
  let cleanNumber = phoneNumber.replace(/@[cg]\.us$/, '');
  
  // If it's still not a number, return as is
  if (!/^\d+$/.test(cleanNumber)) {
    return phoneNumber;
  }
  
  // Add country code formatting for common patterns
  if (cleanNumber.startsWith('91') && cleanNumber.length === 12) {
    // Indian number format: +91 XXXX XXX XXX
    return `+91 ${cleanNumber.slice(2, 6)} ${cleanNumber.slice(6, 9)} ${cleanNumber.slice(9)}`;
  } else if (cleanNumber.startsWith('1') && cleanNumber.length === 11) {
    // US/Canada number format: +1 XXX XXX XXXX
    return `+1 ${cleanNumber.slice(1, 4)} ${cleanNumber.slice(4, 7)} ${cleanNumber.slice(7)}`;
  } else if (cleanNumber.startsWith('44') && cleanNumber.length >= 11) {
    // UK number format: +44 XXXX XXX XXX
    return `+44 ${cleanNumber.slice(2, 6)} ${cleanNumber.slice(6, 9)} ${cleanNumber.slice(9)}`;
  } else {
    // Generic international format: +XX XXXX XXXX...
    if (cleanNumber.length >= 8) {
      const countryCode = cleanNumber.slice(0, 2);
      const rest = cleanNumber.slice(2);
      const formatted = rest.match(/.{1,4}/g)?.join(' ') || rest;
      return `+${countryCode} ${formatted}`;
    }
  }
  
  // Fallback: just add + and space every 4 digits
  return `+${cleanNumber.match(/.{1,4}/g)?.join(' ') || cleanNumber}`;
};

/**
 * Extracts a clean phone number for display
 * @param phoneNumber - Raw phone number
 * @returns Clean number without country code if it's very long
 */
export const getDisplayPhoneNumber = (phoneNumber: string): string => {
  const formatted = formatPhoneNumber(phoneNumber);
  
  // If the formatted number is too long, show just the last part
  if (formatted.length > 20) {
    const cleanNumber = phoneNumber.replace(/@[cg]\.us$/, '');
    if (cleanNumber.length > 10) {
      return cleanNumber.slice(-10); // Show last 10 digits
    }
  }
  
  return formatted;
};
